import { useCallback, useMemo, useState } from 'react'
import { useWorkspace } from '../store/AppContext'
import { chargesForTime, FIXED_FEE_MODELS, formatMoney, workspaceCurrency } from '../lib/billing'
import { useRangeBilling, useRangeEntries } from '../lib/team-data'
import { EmptyState } from '../components/ui'
import { ImportPanel } from '../components/ImportPanel'
import { PeoplePicker } from '../components/PeoplePicker'
import { MultiPicker } from '../components/MultiPicker'
import { dayKey, daysBetween, hrs, keysBetween, parseDayKey, shortDate, weekKeys } from '../lib/time'
import type { ExportGroupKey } from '../lib/api'
import { idsOfKind, liveOfKind } from '../lib/types'
import type { DayKey, Entry } from '../lib/types'

interface BreakEntry {
  id: string
  when: string
  what: string
  sec: number
  amt: number
  bills: boolean
  /**
   * Who logged it -- empty when an ancestor row already names them.
   *
   * Decided once, here, rather than in the render: under Project the middle
   * row IS the person and under Person the top row is, so repeating the name
   * on every entry would be the same word three times down a column. Only the
   * Client view leaves it unanswered.
   */
  who: string
  /**
   * The figure is a flat fee, not hours times a rate.
   *
   * Worth saying on the row, because a six-minute entry charging four figures
   * looks like a mistake until you know why. True for a fee set on the entry
   * itself and for a task priced flat; a PROJECT priced flat is not marked,
   * since that amount is counted once at the project and never reaches here.
   */
  fixed: boolean
}
interface BreakKid {
  name: string
  sec: number
  bill: number
  amt: number
  entries: BreakEntry[]
}
interface BreakRow {
  name: string
  color: string
  sec: number
  bill: number
  amt: number
  kids: Map<string, BreakKid>
}

const RANGES = ['This week', 'Last week', 'This month', 'Last 30 days', 'Custom'] as const

/** The longest custom range, so a mistyped year cannot ask for a decade. */
const MAX_CUSTOM_DAYS = 366
/**
 * The breakdown's top level.
 *
 * "Prospect" is not a fourth way of slicing the same hours -- it changes
 * WHICH hours the whole screen is about. Picking it swaps the entries for
 * prospect time, and the totals, the chart, the donut, the breakdown and
 * both exports follow. The other three never include any of it.
 *
 * That is the point: there is no view here that adds client time to prospect
 * time, because no such figure means anything. A prospect has not agreed to
 * pay for an hour, so counting its hours alongside billable work would
 * overstate what the period earned.
 */
const GROUPS = ['Project', 'Client', 'Person', 'Prospect'] as const

/**
 * The levels the grouped export can stack, in the order Clockify offers them.
 * "Person" is this app's word for what the file calls User.
 */
const VP_LEVELS: ReadonlyArray<{ value: ExportGroupKey; label: string }> = [
  { value: 'client', label: 'Client' },
  { value: 'project', label: 'Project' },
  { value: 'description', label: 'Description' },
  { value: 'task', label: 'Task' },
  { value: 'user', label: 'Person' },
]

/** Client / Project / Description -- the layout the finance file already uses. */
const VP_DEFAULT: (ExportGroupKey | '')[] = ['client', 'project', 'description']
type Range = (typeof RANGES)[number]
type Group = (typeof GROUPS)[number]

export function ReportsScreen() {
  const { ws, me, canSeeAll, proj, client, member, flash, act } = useWorkspace()
  const S = ws.settings
  const [range, setRange] = useState<Range>('This week')
  const [everyone, setEveryone] = useState(false)
  const [group, setGroup] = useState<Group>('Project')
  /*
   * Which rows are open. Keyed by name rather than index so re-sorting -- a
   * filter change, a new entry -- does not open a different row than the one
   * that was clicked. Children are keyed by parent + child for the same reason.
   */
  const [openRows, setOpenRows] = useState<ReadonlySet<string>>(() => new Set())
  const [openKids, setOpenKids] = useState<ReadonlySet<string>>(() => new Set())

  const toggleIn = (set: ReadonlySet<string>, key: string) => {
    const next = new Set(set)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    return next
  }

  // A custom range starts as the current week, so choosing "Custom" shows the
  // report you were already looking at rather than an empty screen you then
  // have to fill in twice.
  const thisWeek = useMemo(() => weekKeys(0, S.weekStart), [S.weekStart])
  const [from, setFrom] = useState<DayKey>(thisWeek[0])
  const [to, setTo] = useState<DayKey>(thisWeek[thisWeek.length - 1])

  // Filters, mirroring what the export understands. Nulls mean "everything",
  // so an untouched bar is the same query the screen had before.
  /** Empty means everyone -- see PeoplePicker. */
  const [fUsers, setFUsers] = useState<readonly string[]>([])
  /*
   * Several at a time, like the people filter beside them.
   *
   * Empty means EVERY one, not none -- the same convention MultiPicker is
   * built around, so "no filter" and "nothing ticked" cannot become two
   * states that look alike and behave differently.
   */
  const [fClients, setFClients] = useState<readonly string[]>([])
  const [fProjects, setFProjects] = useState<readonly string[]>([])
  /**
   * 'fixed' is not a third kind of billable; it is a narrowing of it.
   *
   * An entry on a flat fee is billable by definition -- the models that say
   * so sit below the non_billable branch in the view's CASE -- so this
   * behaves as "billable, and specifically the flat-fee ones".
   */
  const [fBillable, setFBillable] = useState<'all' | 'billable' | 'non' | 'fixed'>('all')

  // Three slots, because three is what fits on one line and what the finance
  // layout needs. Levels 2 and 3 may be empty, which drops the column.
  const [vpGroups, setVpGroups] = useState<(ExportGroupKey | '')[]>(VP_DEFAULT)

  const scopeAll = everyone && canSeeAll

  const keys = useMemo<DayKey[]>(() => {
    const t = new Date()
    if (range === 'Custom') return keysBetween(from, to, MAX_CUSTOM_DAYS)
    if (range === 'This week') return weekKeys(0, S.weekStart)
    if (range === 'Last week') return weekKeys(-1, S.weekStart)
    if (range === 'This month') {
      const n = new Date(t.getFullYear(), t.getMonth() + 1, 0).getDate()
      return Array.from({ length: n }, (_, i) => dayKey(new Date(t.getFullYear(), t.getMonth(), i + 1)))
    }
    return Array.from({ length: 30 }, (_, i) =>
      dayKey(new Date(t.getFullYear(), t.getMonth(), t.getDate() - 29 + i)),
    )
  }, [range, S.weekStart, from, to])

  // keysBetween stops at the cap, so a shorter list than was asked for is how
  // we know to say something rather than silently reporting the wrong period.
  const capped = range === 'Custom' && keys.length === MAX_CUSTOM_DAYS && keys[keys.length - 1] !== to && keys[keys.length - 1] !== from

  // "Everyone" is the only view here that needs other people's time, and it
  // is already bounded by the range picker -- so it is fetched for exactly
  // those days, and "just me" costs nothing extra.
  const { entries: teamEntries, loading: teamLoading } = useRangeEntries(
    ws.id,
    { from: keys[0], to: keys[keys.length - 1] },
    scopeAll,
  )

  // Prices for the same window. Unlike the entries above this is fetched in
  // both scopes, because the "just me" path reads the workspace snapshot and
  // the snapshot carries no prices at all.
  const { billing, loading: billingLoading } = useRangeBilling(
    ws.id,
    { from: keys[0], to: keys[keys.length - 1] },
    true,
  )

  const source = scopeAll ? teamEntries : ws.entries

  /**
   * Whether an entry charges.
   *
   * The database's answer whenever it has arrived -- the same one the CSV and
   * the invoice use. The client-side rule is only the fallback for the moment
   * before the prices land: it agrees about the three flags, it just cannot
   * see rates. `?? ` and not `||`, so a genuine false from the view is not
   * mistaken for a missing answer.
   */
  const charges = useCallback(
    (e: Entry): boolean =>
      billing.get(e.id)?.isBillable ??
      chargesForTime(e.billable, member(e.userId)?.billable, proj(e.projectId)?.billable),
    [billing, member, proj],
  )

  /**
   * Which population this report is about, from the tab that is open.
   *
   * An entry reaches its client through its project, so an entry with no
   * project has no client and no kind: it stays on the client side, where
   * unassigned time has always been counted.
   */
  const prospecting = group === 'Prospect'

  const inScope = useMemo(() => {
    const ids = idsOfKind(ws.clients, prospecting ? 'prospect' : 'client')
    return (clientId: string | null | undefined) =>
      clientId === null || clientId === undefined ? !prospecting : ids.has(clientId)
  }, [ws.clients, prospecting])

  const rows = useMemo<Entry[]>(() => {
    const set = new Set(keys)
    return source.filter((e) => {
      if (!set.has(e.day)) return false
      // First, and not optional: the other population is not in this report
      // at all, whatever the filters below say.
      if (!inScope(e.projectId ? ws.projects.find((x) => x.id === e.projectId)?.clientId : null)) {
        return false
      }
      if (!scopeAll && e.userId !== me.id) return false
      if (fUsers.length && !fUsers.includes(e.userId)) return false
      if (fProjects.length && (!e.projectId || !fProjects.includes(e.projectId))) return false
      if (fClients.length) {
        // A client is reached through its projects, so an entry with no
        // project cannot belong to one.
        const p = e.projectId ? ws.projects.find((x) => x.id === e.projectId) : null
        if (!p || !p.clientId || !fClients.includes(p.clientId)) return false
      }
      const chargeable = charges(e)
      if (fBillable === 'billable' && !chargeable) return false
      if (fBillable === 'non' && chargeable) return false
      if (fBillable === 'fixed') {
        // Read from the view, like the amount and the row's own tag. Working
        // it out here from the entry's columns would miss a task or project
        // price, which the entry itself knows nothing about.
        const model = billing.get(e.id)?.pricingModel ?? ''
        if (!FIXED_FEE_MODELS.includes(model as (typeof FIXED_FEE_MODELS)[number])) return false
      }
      return true
    })
  }, [source, ws.projects, keys, scopeAll, me.id, fUsers, fProjects, fClients, fBillable, charges, inScope, billing])

  const totalSec = rows.reduce((a, e) => a + e.dur, 0)
  const billSec = rows.filter(charges).reduce((a, e) => a + e.dur, 0)

  /**
   * What one entry is worth, as the database priced it.
   *
   * This used to resolve the person's rate for the entry's date and fall back
   * to the project's. Those are the LAST TWO steps of entry_billing's rule:
   * imported rate, then task fixed price, then project fixed price, then user
   * hourly, then project hourly. The first three were invisible here -- the
   * fixed-price tables were never loaded and an imported rate is not on the
   * Entry type -- so an imported entry was priced at the person's current
   * rate on screen and at the rate from the CSV in the export.
   *
   * Null still means "no figure belongs on this row": no rate set, or a flat
   * project price that is reported once rather than smeared across entries.
   * A miss while the prices are still loading is null too, which is why the
   * amount is withheld rather than shown as zero until they arrive.
   */
  const entryAmount = useCallback(
    (e: Entry): number | null => billing.get(e.id)?.amount ?? null,
    [billing],
  )

  const amount = rows.reduce((a, e) => a + (entryAmount(e) ?? 0), 0)
  const unpriced = rows.filter((e) => charges(e) && entryAmount(e) === null).length
  // Whatever the database priced in, so the label matches the figure. The
  // old `ws.rates[0]` took an arbitrary rate row, which need not be the rate
  // that was actually applied -- or even the same currency as it.
  const currency = useMemo(() => {
    for (const r of billing.values()) if (r.currency) return r.currency
    return workspaceCurrency(ws.rates)
  }, [billing, ws.rates])
  const activeDays = Math.max(1, keys.filter((k) => rows.some((e) => e.day === k)).length)
  const pctBill = totalSec ? Math.round((billSec / totalSec) * 100) : 0

  const dayTotals = keys.map((k) => rows.filter((e) => e.day === k).reduce((a, e) => a + e.dur, 0))
  const compact = keys.length > 14
  // Past roughly two months there is no room for a label per day, nor for the
  // gaps between bars -- a year at 6px apart is 2000px of gap alone. The
  // chart becomes a density strip, with the dates named underneath instead.
  const dense = keys.length > 62
  const today = dayKey(new Date())

  /**
   * What the chart actually plots.
   *
   * A bar per day stops working long before a year: on a phone, 366 columns
   * in 320 pixels leaves each one less than a pixel wide, and the gaps
   * between them eat the chart entirely. Past two months the days are summed
   * into weeks instead -- around 52 bars, which fit on any screen and say the
   * same thing about where the hours went.
   */
  const series = useMemo(() => {
    if (!dense) return { keys, totals: dayTotals, span: 1 }
    const bKeys: DayKey[] = []
    const bTotals: number[] = []
    for (let i = 0; i < keys.length; i += 7) {
      bKeys.push(keys[i])
      bTotals.push(dayTotals.slice(i, i + 7).reduce((a, n) => a + n, 0))
    }
    return { keys: bKeys, totals: bTotals, span: 7 }
  }, [dense, keys, dayTotals])

  const maxSec = Math.max(3600, ...series.totals)

  /* ---- donut ---- */
  /**
   * Hours per CLIENT, not per project.
   *
   * A client is the thing an invoice is addressed to, and the project list
   * said less than it appeared to: where several clients each keep a project
   * called "FYE2025 Accounts and Annual Return", the chart printed that name
   * twice with nothing to say whose was whose, and split one client's hours
   * across as many slices as they had projects.
   *
   * A client carries no colour of its own, so it borrows the colour of the
   * project it spent the most time on. That is stable as the range moves --
   * unlike an index into a palette, which would repaint every client the
   * moment the order changed -- and it leaves a client with one project
   * looking exactly as it did before.
   */
  const byClient = useMemo(() => {
    const bucket = new Map<
      string,
      { name: string; sec: number; projects: Map<string, number> }
    >()

    for (const e of rows) {
      const p = proj(e.projectId)
      const c = p ? client(p.clientId) : null
      const id = c?.id ?? 'none'

      let row = bucket.get(id)
      if (!row) {
        row = {
          name: c?.name ?? (prospecting ? 'No prospect' : 'No client'),
          sec: 0,
          projects: new Map(),
        }
        bucket.set(id, row)
      }
      row.sec += e.dur
      if (p) row.projects.set(p.id, (row.projects.get(p.id) ?? 0) + e.dur)
    }

    return [...bucket.values()]
      .map((r) => {
        // Biggest project takes the colour; a tie goes to the lower id so the
        // same data never paints two different ways.
        let best: { id: string; sec: number } | null = null
        for (const [id, sec] of r.projects) {
          if (!best || sec > best.sec || (sec === best.sec && id < best.id)) best = { id, sec }
        }
        return {
          name: r.name,
          color: (best ? proj(best.id)?.color : null) ?? '#CFCABC',
          sec: r.sec,
        }
      })
      .sort((a, b) => b.sec - a.sec)
  }, [rows, prospecting, proj, client])

  // Each slice's start is the sum of the ones before it, derived rather than
  // accumulated: mutating a variable during render is not safe once React can
  // re-enter one. The list is a handful of clients, so the repeated sum
  // costs nothing.
  const stops = useMemo(() => {
    const angles = byClient.map((r) => (totalSec ? (r.sec / totalSec) * 360 : 0))
    return byClient.map((r, i) => {
      const from = angles.slice(0, i).reduce((sum, a) => sum + a, 0)
      return `${r.color} ${from.toFixed(1)}deg ${(from + angles[i]).toFixed(1)}deg`
    })
  }, [byClient, totalSec])

  /* ---- breakdown ---- */
  /**
   * The breakdown, three levels deep.
   *
   * The top level is whatever is grouped by; under it sits the next natural
   * level, and under that the individual entries. Grouping by Client answers
   * "which clients" at the top, "which projects for this client" when opened,
   * and "what was logged, when, and what it was charged" at the bottom --
   * which is the question a total on its own never answers.
   *
   * Every level is summed from the SAME entries, so an opened row always adds
   * up to the row above it. Nothing is recomputed from a rounded figure.
   */
  const breakdown = useMemo(() => {
    const top = new Map<string, BreakRow>()

    for (const e of rows) {
      const p = proj(e.projectId)
      const projectName = p?.name ?? 'No project'
      const clientName =
        (p ? client(p.clientId)?.name : null) ?? (prospecting ? 'No prospect' : 'No client')
      const personName = member(e.userId)?.name ?? 'Unknown'

      let key: string
      let color: string
      let midName: string
      if (group === 'Prospect') {
        // Same shape as Client, over the other population: which prospects,
        // then which of their projects, then what was logged. The hours are
        // what has been spent chasing the work, which is the figure worth
        // seeing before deciding whether to keep chasing it.
        key = clientName
        color = p?.color ?? '#CFCABC'
        midName = projectName
      } else if (group === 'Project') {
        key = projectName
        color = p?.color ?? '#CFCABC'
        midName = personName
      } else if (group === 'Client') {
        key = clientName
        color = p?.color ?? '#CFCABC'
        midName = projectName
      } else {
        key = personName
        color = '#1A1913'
        // Client, not project. A person's work reads as "who am I billing",
        // and a list of project names does not answer that -- several of them
        // can belong to one client, so the same client appeared four times
        // under one person with the time split between the rows.
        midName = clientName
      }

      const amt = entryAmount(e) ?? 0
      const bills = charges(e)
      // Read from the view, like the amount beside it: whether a figure is a
      // flat fee is part of how the database priced the row, and working it
      // out again here from the entry's own columns is how the screen and the
      // CSV came to disagree before.
      const model = billing.get(e.id)?.pricingModel
      const fixed = model === 'entry_fixed' || model === 'task_fixed'

      let row = top.get(key)
      if (!row) {
        row = { name: key, color, sec: 0, bill: 0, amt: 0, kids: new Map() }
        top.set(key, row)
      }
      row.sec += e.dur
      if (bills) {
        row.bill += e.dur
        row.amt += amt
      }

      let kid = row.kids.get(midName)
      if (!kid) {
        kid = { name: midName, sec: 0, bill: 0, amt: 0, entries: [] }
        row.kids.set(midName, kid)
      }
      kid.sec += e.dur
      if (bills) {
        kid.bill += e.dur
        kid.amt += amt
      }
      kid.entries.push({
        id: e.id,
        when: shortDate(e.day),
        what: e.desc.trim() || 'No description',
        sec: e.dur,
        amt: bills ? amt : 0,
        bills,
        who: group === 'Client' || group === 'Prospect' ? personName : '',
        fixed,
      })
    }

    const byTime = <T extends { sec: number }>(a: T, b: T) => b.sec - a.sec

    return [...top.values()].sort(byTime).map((r) => ({
      ...r,
      kids: [...r.kids.values()]
        .sort(byTime)
        .map((k) => ({ ...k, entries: [...k.entries].sort(byTime) })),
    }))
  }, [rows, group, prospecting, proj, client, member, entryAmount, charges, billing])

  // The server builds the CSV, so an owner exports the whole team's cost in one request.
  const exportCsv = () => {
    if (!rows.length) {
      flash('Nothing to export in this range')
      return
    }
    act.exportCsv(keys[0], keys[keys.length - 1], scopeAll ? 'all' : 'mine', {
      kind: prospecting ? 'prospect' : 'client',
      userIds: fUsers,
      clientIds: fClients,
      projectIds: fProjects,
      billable: fBillable === 'all' || fBillable === 'fixed' ? null : fBillable === 'billable',
      pricingModels: fBillable === 'fixed' ? FIXED_FEE_MODELS : null,
    })
    flash(`Exporting ${rows.length} ${rows.length === 1 ? 'entry' : 'entries'}`)
  }

  /**
   * The grouped export finance reads.
   *
   * Same range, same filters and same permission check as the detailed one --
   * only the shaping differs, so the two files can never disagree about what
   * period they cover.
   */
  const vpExport = () => {
    if (!rows.length) {
      flash('Nothing to export in this range')
      return
    }
    const groupBy = vpGroups.filter((g): g is ExportGroupKey => g !== '')
    if (!groupBy.length) {
      flash('Pick at least one level to group by')
      return
    }
    act.exportCsv(keys[0], keys[keys.length - 1], scopeAll ? 'all' : 'mine', {
      kind: prospecting ? 'prospect' : 'client',
      userIds: fUsers,
      clientIds: fClients,
      projectIds: fProjects,
      billable: fBillable === 'all' || fBillable === 'fixed' ? null : fBillable === 'billable',
      pricingModels: fBillable === 'fixed' ? FIXED_FEE_MODELS : null,
      layout: 'summary',
      groupBy,
    })
    flash('Exporting ' + rows.length + (rows.length === 1 ? ' entry' : ' entries') + ', grouped')
  }

  /** A level already used in another slot cannot be picked twice. */
  const levelsFor = (slot: number) =>
    VP_LEVELS.filter((l) => !vpGroups.some((g, i) => i !== slot && g === l.value))

  const setLevel = (slot: number, value: ExportGroupKey | '') =>
    setVpGroups((prev) => prev.map((g, i) => (i === slot ? value : g)))

  // "Nothing was tracked custom" is not a sentence; name the actual dates.
  const rangeLabel =
    range === 'Custom'
      ? `between ${parseDayKey(keys[0]).toLocaleDateString()} and ${parseDayKey(
          keys[keys.length - 1],
        ).toLocaleDateString()}`
      : range.toLowerCase()

  /** This side's clients, for the filter. Binned ones are not choices. */
  const clientItems = useMemo(
    () =>
      liveOfKind(ws.clients, prospecting ? 'prospect' : 'client').map((c) => ({
        id: c.id,
        name: c.name,
        // So typing a contact finds the client, as the people filter does
        // with an email.
        search: c.contact,
      })),
    [ws.clients, prospecting],
  )

  /**
   * The projects worth offering.
   *
   * Narrowed by the clients chosen above, so picking a client and then
   * opening this list shows that client's projects rather than all 62 --
   * which is the order people work in. The client's name rides along as
   * search text, so "C100" finds its projects by name too.
   */
  const projectItems = useMemo(() => {
    const byClient = new Map(ws.clients.map((c) => [c.id, c.name]))
    return ws.projects
      .filter((p) => inScope(p.clientId))
      .filter((p) => !fClients.length || (p.clientId && fClients.includes(p.clientId)))
      .map((p) => ({
        id: p.id,
        name: p.name,
        search: p.clientId ? (byClient.get(p.clientId) ?? '') : '',
      }))
  }, [ws.projects, ws.clients, inScope, fClients])

  /**
   * The breakdown's tab row, defined once and rendered in both branches.
   *
   * It has to appear whether or not the range has anything in it: it is the
   * only control that changes which grouping is shown, so a view without it
   * is a view you cannot leave.
   */
  const groupTabs = (
    <div
      className="row"
      style={{ gap: 8, padding: '13px 16px', borderBottom: '1px solid var(--line2)' }}
    >
      <div className="card-title" style={{ marginRight: 6 }}>
        Breakdown
      </div>
      {GROUPS.map((g) => (
        <button
          key={g}
          type="button"
          className="softchip"
          aria-pressed={group === g}
          title={
            g === 'Prospect'
              ? 'Time logged against prospects. Reported on its own: none of it is in the other three.'
              : undefined
          }
          onClick={() => {
            // Crossing between the two populations invalidates any client or
            // project filter, which names something that does not exist on
            // the other side -- left alone, the report would come back empty
            // and look broken.
            if ((g === 'Prospect') !== (group === 'Prospect')) {
              setFClients([])
              setFProjects([])
            }
            setGroup(g)
          }}
        >
          {g}
        </button>
      ))}
    </div>
  )

  const kpis = [
    { label: 'Total tracked', value: hrs(totalSec), delta: `${keys.length} day range` },
    { label: 'Billable', value: hrs(billSec), delta: `${pctBill}% of tracked` },
    {
      label: 'Amount',
      value: billingLoading ? '—' : formatMoney(amount, currency),
      delta: billingLoading
        ? 'pricing…'
        : unpriced > 0
          ? `${unpriced} entr${unpriced === 1 ? 'y has' : 'ies have'} no rate`
          : 'at charge-out rates',
    },
    { label: 'Daily average', value: hrs(totalSec / activeDays), delta: `over ${activeDays} active day${activeDays === 1 ? '' : 's'}` },
  ]

  return (
    <section className="screen" data-scope={prospecting ? 'prospect' : undefined}>
      {/* ---------- filters ---------- */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        {RANGES.map((r) => (
          <button key={r} type="button" className="chip" aria-pressed={range === r} onClick={() => setRange(r)}>
            {r}
          </button>
        ))}

        {/* Shown only for Custom, so the common case keeps its one-click bar.
            These drive the report AND the export -- the file is whatever is
            on screen, which is the rule the filters below follow too. */}
        {range === 'Custom' && (
          <>
            <label className="sr-only" htmlFor="range-from">
              From date
            </label>
            <input
              id="range-from"
              type="date"
              className="input input--date"
              value={from}
              onChange={(e) => {
                const v = e.target.value
                if (!v) return
                setFrom(v)
                // Drag the other end along rather than refusing the date.
                // Capping the inputs at each other means moving a range
                // forward has to be done in a particular order, which is a
                // rule nobody should have to learn.
                if (daysBetween(v, to) > 0) setTo(v)
              }}
            />
            <span style={{ fontSize: 12.5, color: 'var(--mut)' }}>to</span>
            <label className="sr-only" htmlFor="range-to">
              To date
            </label>
            <input
              id="range-to"
              type="date"
              className="input input--date"
              value={to}
              onChange={(e) => {
                const v = e.target.value
                if (!v) return
                setTo(v)
                if (daysBetween(from, v) > 0) setFrom(v)
              }}
            />
          </>
        )}
        {canSeeAll && (
          <>
            <div className="divider-v" />
            <button type="button" className="chip chip--ac" aria-pressed={!everyone} onClick={() => setEveryone(false)}>
              Just me
            </button>
            <button type="button" className="chip chip--ac" aria-pressed={everyone} onClick={() => setEveryone(true)}>
              Everyone
            </button>
          </>
        )}
        <button type="button" className="btn push" onClick={exportCsv}>
          ↓ Export CSV
        </button>
      </div>

      {prospecting && (
        <p
          className="pretty"
          style={{ fontSize: 12.5, color: 'var(--dg)', margin: '-4px 0 0' }}
          role="status"
        >
          Showing <b>prospect</b> time only — hours spent chasing work that is not won yet.
          None of it counts towards client hours or billing, and none of it is in the client
          export. The totals, the chart and the exports below all follow this tab.
        </p>
      )}

      {capped && (
        <p style={{ fontSize: 12.5, color: 'var(--dg)', margin: '-4px 0 0' }} role="status">
          That range is longer than {MAX_CUSTOM_DAYS} days, so the report and the export stop at{' '}
          {parseDayKey(keys[keys.length - 1]).toLocaleDateString()}.
        </p>
      )}

      {/* Filters. What you see here is exactly what the export contains --
          the same values drive both, so the file can never disagree with the
          screen it was taken from. */}
      <div
        className="card card--pad"
        style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}
      >
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--mut)', letterSpacing: '.04em' }}>
          FILTER
        </span>

        {canSeeAll && (
          <PeoplePicker team={ws.team} selected={fUsers} onChange={setFUsers} />
        )}

        {/* This side's names only. Offering the others would be offering a
            filter that can only ever empty the report. */}
        <MultiPicker
          items={clientItems}
          selected={fClients}
          onChange={(ids) => {
            /*
             * Drop any project that the new client choice no longer offers.
             *
             * The project list below narrows to the chosen clients, so a
             * project picked first and then filtered out would go on
             * narrowing the report from a list it is no longer in -- rows
             * missing, and the control that removed them not showing it.
             */
            if (ids.length) {
              const keep = new Set(
                ws.projects
                  .filter((p) => p.clientId && ids.includes(p.clientId))
                  .map((p) => p.id),
              )
              setFProjects((prev) => prev.filter((id) => keep.has(id)))
            }
            setFClients(ids)
          }}
          allLabel={prospecting ? 'Prospect — all' : 'Client — all'}
          unit={prospecting ? ['prospect', 'prospects'] : ['client', 'clients']}
          searchPlaceholder={prospecting ? 'Search prospects…' : 'Search clients…'}
          ariaLabel={prospecting ? 'Filter by prospect' : 'Filter by client'}
          emptyText={prospecting ? 'No prospects yet.' : 'No clients yet.'}
        />

        <MultiPicker
          items={projectItems}
          selected={fProjects}
          onChange={setFProjects}
          allLabel="Project — all"
          unit={['project', 'projects']}
          searchPlaceholder="Search projects…"
          ariaLabel="Filter by project"
          emptyText="No projects to choose from."
        />

        <select
          className="role-select"
          aria-label="Filter by billable status"
          value={fBillable}
          onChange={(e) => setFBillable(e.target.value as typeof fBillable)}
        >
          <option value="all">Status — all</option>
          <option value="billable">Billable only</option>
          <option value="non">Non-billable only</option>
          <option value="fixed">Fixed fee only</option>
        </select>

        {(fUsers.length > 0 || fClients.length > 0 || fProjects.length > 0 || fBillable !== 'all') && (
          <button
            type="button"
            className="linkbtn"
            style={{ color: 'var(--mut)' }}
            onClick={() => {
              setFUsers([])
              setFClients([])
              setFProjects([])
              setFBillable('all')
            }}
          >
            Clear filters
          </button>
        )}

        <span className="push" style={{ fontSize: 12.5, color: 'var(--mut)' }}>
          {rows.length} {rows.length === 1 ? 'entry' : 'entries'}
        </span>
      </div>

      {/* The grouped export. Separate from the Breakdown control below, which
          only reshapes what is on screen -- this one decides the columns in
          the file finance receives. */}
      <div
        className="card card--pad"
        style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}
      >
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--mut)', letterSpacing: '.04em' }}>
          GROUP BY
        </span>

        {vpGroups.map((value, slot) => (
          <select
            key={slot}
            className="role-select"
            aria-label={'Grouping level ' + (slot + 1)}
            value={value}
            onChange={(e) => setLevel(slot, e.target.value as ExportGroupKey | '')}
          >
            {/* The first level is what makes it a report at all, so only the
                later slots can be switched off. */}
            {slot > 0 && <option value="">— none —</option>}
            {levelsFor(slot).map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </select>
        ))}

        <button type="button" className="btn btn--primary push" onClick={vpExport}>
          ↓ VP Export
        </button>
      </div>

      <p style={{ fontSize: 12.5, color: 'var(--mut)', margin: '-6px 0 0' }}>
        One row per combination, for the range and filters above. Entries sharing every
        grouping value are added together.
      </p>

      {canSeeAll && <ImportPanel />}

      {teamLoading || (fBillable === 'fixed' && billingLoading) ? (
        <EmptyState title="Loading the team’s time…" body="Fetching entries for this range." />
      ) : !rows.length ? (
        /*
         * The tabs stay, and the empty state goes inside the card with them.
         *
         * They used to live only in the branch that had rows, so choosing a
         * tab with nothing under it removed the control that had just been
         * used -- and with it every way back. Prospect made that reachable in
         * one click, because a workspace can easily have no prospect time at
         * all, but Person or Client would have done the same on a quiet week.
         *
         * A control that can put the screen into a state it cannot leave is
         * the bug; the empty range is not.
         */
        <div className="card card--clip">
          {groupTabs}
          {/* Not EmptyState here: that carries its own dashed border, which
              inside a card reads as a box in a box. The words are the same;
              the frame is the card's. */}
          <div style={{ padding: '44px 20px', textAlign: 'center', color: 'var(--mut)' }}>
            <h2 style={{ fontSize: 15, fontWeight: 500, color: 'var(--ink)', margin: '0 0 6px' }}>
              {prospecting ? 'No prospect time in this range' : 'No time in this range'}
            </h2>
            <p className="pretty" style={{ fontSize: 13.5, margin: 0 }}>
              {prospecting
                ? `No time was logged against a prospect ${rangeLabel}${scopeAll ? ' by anyone' : ''}. Prospect time is tracked on the Prospects screen — or pick another breakdown above to see client work.`
                : `Nothing was tracked ${rangeLabel}${scopeAll ? ' by anyone' : ''}. Track some time, then come back — the charts and breakdown fill in automatically.`}
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* ---------- KPIs ---------- */}
          <div className="kpi-grid">
            {kpis.map((k) => (
              <div key={k.label} className="kpi">
                <div className="eyebrow">{k.label}</div>
                <div className="kpi-val">{k.value}</div>
                <div className="kpi-delta">{k.delta}</div>
              </div>
            ))}
          </div>

          {/* ---------- charts ---------- */}
          <div className="chart-grid">
            <div className="card" style={{ padding: '18px 18px 12px' }}>
              <div className="spread" style={{ marginBottom: 18 }}>
                <div className="card-title">Tracked over time</div>
                <div className="mono" style={{ fontSize: 12.5, color: 'var(--mut)' }}>
                  peak {hrs(maxSec)}
                  {dense ? ' / week' : ''}
                </div>
              </div>
              {ws.appearance.reportsChart === 'line' ? (
                <LineChart
                  keys={series.keys}
                  totals={series.totals}
                  max={maxSec}
                  compact={compact}
                  dense={dense}
                />
              ) : (
                <div className="bars" style={{ gap: compact ? 3 : 6 }}>
                  {series.keys.map((k, i) => {
                    const d = parseDayKey(k)
                    const end = keys[Math.min(keys.length - 1, i * series.span + series.span - 1)]
                    const pct = Math.round((series.totals[i] / maxSec) * 100)
                    const isNow =
                      series.span === 1 ? k === today : today >= k && today <= end
                    return (
                      <div
                        key={k}
                        className="bar-col"
                        title={
                          series.span === 1
                            ? `${d.toLocaleDateString()} — ${hrs(series.totals[i])}`
                            : `${d.toLocaleDateString()} – ${parseDayKey(end).toLocaleDateString()} — ${hrs(series.totals[i])}`
                        }
                      >
                        <div className="bar-track">
                          <div
                            className="bar-fill"
                            style={{
                              height: `${Math.max(2, pct)}%`,
                              background: isNow ? 'var(--ac)' : series.totals[i] ? 'var(--ink)' : 'var(--line2)',
                              opacity: series.totals[i] ? 1 : 0.6,
                            }}
                          />
                        </div>
                        {!dense && (
                          <div className="bar-caption">
                            {compact
                              ? d.getDate() % 5 === 0
                                ? String(d.getDate())
                                : ''
                              : d.toLocaleDateString(undefined, { weekday: 'narrow' })}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
              {dense && <RangeEnds keys={keys} />}
            </div>

            <div className="card card--pad">
              <div className="card-title" style={{ marginBottom: 16 }}>
                Share of hours
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 22 }}>
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <div
                    className="donut"
                    role="img"
                    aria-label={`Share of hours by ${prospecting ? 'prospect' : 'client'}. ${byClient
                      .slice(0, 6)
                      .map((r) => `${r.name} ${hrs(r.sec)}`)
                      .join(', ')}`}
                    style={{
                      background: `conic-gradient(${stops.length ? stops.join(',') : 'var(--line2) 0deg 360deg'})`,
                      mask: 'radial-gradient(circle, transparent 54%, #000 55%)',
                      WebkitMask: 'radial-gradient(circle, transparent 54%, #000 55%)',
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <div className="mono" style={{ fontSize: 18, fontWeight: 600 }}>
                      {Math.round(totalSec / 3600)}h
                    </div>
                    <div className="eyebrow" style={{ fontSize: 10.5 }}>
                      total
                    </div>
                  </div>
                </div>
                <div style={{ flex: '1 1 170px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 9 }}>
                  {byClient.slice(0, 6).map((r) => (
                    <div key={r.name} className="row" style={{ gap: 9 }}>
                      <span className="dot" style={{ background: r.color }} aria-hidden />
                      <span className="trunc" style={{ flex: 1, minWidth: 0, fontSize: 13 }}>
                        {r.name}
                      </span>
                      <span className="mono" style={{ fontSize: 12.5, color: 'var(--mut)' }}>
                        {totalSec ? Math.round((r.sec / totalSec) * 100) : 0}%
                      </span>
                      <span className="mono" style={{ fontSize: 13, fontWeight: 500, minWidth: 52, textAlign: 'right' }}>
                        {hrs(r.sec)}
                      </span>
                    </div>
                  ))}
                  {byClient.length > 6 && (
                    <div style={{ fontSize: 12, color: 'var(--faint)' }}>+{byClient.length - 6} more</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ---------- breakdown ---------- */}
          <div className="card card--clip">
            {groupTabs}
            <div style={{ overflowX: 'auto' }}>
              <div style={{ minWidth: 620 }}>
                <div className="report-grid eyebrow" style={{ padding: '10px 16px', background: 'var(--surf2)', borderBottom: '1px solid var(--line2)' }}>
                  <div>{group}</div>
                  <div>Share</div>
                  <div style={{ textAlign: 'right' }}>Billable</div>
                  <div style={{ textAlign: 'right' }}>Hours</div>
                  <div style={{ textAlign: 'right' }}>Amount</div>
                </div>
                {breakdown.map((b) => {
                  const rowOpen = openRows.has(b.name)
                  return (
                    <div key={b.name}>
                      <div className="report-grid report-row">
                        <div className="row" style={{ gap: 7, minWidth: 0 }}>
                          {/* The whole name is the control: a 9px triangle is a
                              poor target and there is nothing else to click. */}
                          <button
                            type="button"
                            className="linkbtn row"
                            aria-expanded={rowOpen}
                            style={{ gap: 7, minWidth: 0, color: 'inherit', textAlign: 'left' }}
                            onClick={() => setOpenRows((o) => toggleIn(o, b.name))}
                          >
                            <span
                            aria-hidden
                            style={{
                              fontSize: 9,
                              color: 'var(--mut)',
                              width: 9,
                              flexShrink: 0,
                              display: 'inline-block',
                              transform: rowOpen ? 'none' : 'rotate(-90deg)',
                            }}
                          >
                            ▾
                          </span>
                            <span className="dot" style={{ background: b.color }} aria-hidden />
                            <span className="trunc" style={{ fontSize: 13.5 }}>
                              {b.name}
                            </span>
                            <span style={{ fontSize: 11.5, color: 'var(--faint)', flexShrink: 0 }}>
                              {b.kids.length}
                            </span>
                          </button>
                        </div>
                        <div style={{ paddingRight: 18 }}>
                          <div className="track track--thin">
                            <div
                              style={{
                                width: `${totalSec ? (b.sec / totalSec) * 100 : 0}%`,
                                background: b.color,
                              }}
                            />
                          </div>
                        </div>
                        <div className="mono" style={{ textAlign: 'right', fontSize: 13, color: 'var(--mut)' }}>
                          {hrs(b.bill)}
                        </div>
                        <div className="mono" style={{ textAlign: 'right', fontSize: 13.5, fontWeight: 500 }}>
                          {hrs(b.sec)}
                        </div>
                        <div className="mono" style={{ textAlign: 'right', fontSize: 13.5 }}>
                          {formatMoney(b.amt, currency)}
                        </div>
                      </div>

                      {rowOpen &&
                        b.kids.map((k) => {
                          const kidKey = b.name + ' > ' + k.name
                          const kidOpen = openKids.has(kidKey)
                          return (
                            <div key={kidKey}>
                              <div
                                className="report-grid report-row"
                                style={{ background: 'var(--surf2)' }}
                              >
                                <div
                                  className="row"
                                  style={{ gap: 7, minWidth: 0, paddingLeft: 18, gridColumn: 'span 2' }}
                                >
                                  <button
                                    type="button"
                                    className="linkbtn row"
                                    aria-expanded={kidOpen}
                                    style={{ gap: 7, minWidth: 0, color: 'inherit', textAlign: 'left' }}
                                    onClick={() => setOpenKids((o) => toggleIn(o, kidKey))}
                                  >
                                    <span
                            aria-hidden
                            style={{
                              fontSize: 9,
                              color: 'var(--mut)',
                              width: 9,
                              flexShrink: 0,
                              display: 'inline-block',
                              transform: kidOpen ? 'none' : 'rotate(-90deg)',
                            }}
                          >
                            ▾
                          </span>
                                    <span className="trunc" style={{ fontSize: 13 }}>
                                      {k.name}
                                    </span>
                                    <span style={{ fontSize: 11.5, color: 'var(--faint)', flexShrink: 0 }}>
                                      {k.entries.length}
                                    </span>
                                  </button>
                                </div>
                                <div className="mono" style={{ textAlign: 'right', fontSize: 12.5, color: 'var(--mut)' }}>
                                  {hrs(k.bill)}
                                </div>
                                <div className="mono" style={{ textAlign: 'right', fontSize: 13 }}>
                                  {hrs(k.sec)}
                                </div>
                                <div className="mono" style={{ textAlign: 'right', fontSize: 13 }}>
                                  {formatMoney(k.amt, currency)}
                                </div>
                              </div>

                              {kidOpen &&
                                k.entries.map((en) => (
                                  <div
                                    key={en.id}
                                    className="report-grid report-row"
                                    style={{ background: 'var(--canvas)' }}
                                  >
                                    <div
                                      className="row"
                                      style={{ gap: 8, minWidth: 0, paddingLeft: 44, gridColumn: 'span 2' }}
                                    >
                                      <span
                                        className="mono"
                                        style={{ fontSize: 11.5, color: 'var(--faint)', flexShrink: 0 }}
                                      >
                                        {en.when}
                                      </span>
                                      {en.who && (
                                        <span className="report-who">{en.who}</span>
                                      )}
                                      {/* Still truncates eventually -- a description can be
                                          any length -- so the whole of it is on hover. */}
                                      <span
                                        className="trunc"
                                        style={{ fontSize: 12.5, color: 'var(--mut)' }}
                                        title={en.what}
                                      >
                                        {en.what}
                                      </span>
                                      {en.fixed && (
                                        <span
                                          className="entry-tag"
                                          style={{ fontSize: 10.5, flexShrink: 0 }}
                                          title="A flat fee for this entry. The duration beside it did not decide the amount."
                                        >
                                          fixed fee
                                        </span>
                                      )}
                                    </div>
                                    {/* Said once, rather than a billable 0.00h
                                        that reads as a missing figure. */}
                                    <div
                                      className="mono"
                                      style={{ textAlign: 'right', fontSize: 12, color: 'var(--faint)' }}
                                    >
                                      {en.bills ? hrs(en.sec) : 'non-billable'}
                                    </div>
                                    <div className="mono" style={{ textAlign: 'right', fontSize: 12.5 }}>
                                      {hrs(en.sec)}
                                    </div>
                                    <div className="mono" style={{ textAlign: 'right', fontSize: 12.5 }}>
                                      {en.bills ? formatMoney(en.amt, currency) : '—'}
                                    </div>
                                  </div>
                                ))}
                            </div>
                          )
                        })}
                    </div>
                  )
                })}
                <div className="report-grid" style={{ padding: '12px 16px', background: 'var(--surf2)', fontWeight: 600 }}>
                  <div style={{ fontSize: 13.5 }}>Total</div>
                  <div />
                  <div className="mono" style={{ textAlign: 'right', fontSize: 13 }}>
                    {hrs(billSec)}
                  </div>
                  <div className="mono" style={{ textAlign: 'right', fontSize: 14 }}>
                    {hrs(totalSec)}
                  </div>
                  <div className="mono" style={{ textAlign: 'right', fontSize: 14 }}>
                    {billingLoading ? '—' : formatMoney(amount, currency)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  )
}

/* ------------------------------------------------------------------ */

/** The two ends of a range, for charts too long to label day by day. */
function RangeEnds({ keys }: { keys: DayKey[] }) {
  return (
    <div
      className="spread"
      style={{ fontSize: 11, color: 'var(--faint)', fontFamily: 'var(--mono)', paddingTop: 6 }}
    >
      <span>{parseDayKey(keys[0]).toLocaleDateString()}</span>
      <span>{parseDayKey(keys[keys.length - 1]).toLocaleDateString()}</span>
    </div>
  )
}

function LineChart({
  keys,
  totals,
  max,
  compact,
  dense,
}: {
  keys: DayKey[]
  totals: number[]
  max: number
  compact: boolean
  dense: boolean
}) {
  const pts = totals.map((s, i) => {
    const x = keys.length > 1 ? (i / (keys.length - 1)) * 598 + 1 : 300
    const y = 155 - (s / max) * 148
    return `${x.toFixed(1)},${y.toFixed(1)}`
  })
  return (
    <div style={{ position: 'relative', height: 180 }}>
      <svg viewBox="0 0 600 160" preserveAspectRatio="none" style={{ width: '100%', height: 150, display: 'block' }} aria-hidden>
        <polygon points={`1,158 ${pts.join(' ')} 599,158`} fill="var(--ac-soft)" />
        <polyline
          points={pts.join(' ')}
          fill="none"
          stroke="var(--ac)"
          strokeWidth={2.5}
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      {dense ? (
        <RangeEnds keys={keys} />
      ) : (
        <div style={{ display: 'flex', gap: compact ? 3 : 6 }}>
          {keys.map((k) => {
            const d = parseDayKey(k)
            return (
              <div key={k} className="bar-label">
                {compact ? (d.getDate() % 5 === 0 ? String(d.getDate()) : '') : d.toLocaleDateString(undefined, { weekday: 'narrow' })}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
